import cytoscape, { type Core, type ElementDefinition } from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';
import { Download, Maximize2, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/cn';

import { colorForClass, LEGEND_ORDER, OTHER_CLASS_COLOR } from './colors';

cytoscape.use(coseBilkent);

// ─── Public types ───────────────────────────────────────────────

export type NodeVisualState = 'committed' | 'staged' | 'staged_failing' | 'inferred';

export interface KGNode {
  id: string;
  label: string;
  className: string;
  state?: NodeVisualState;
}

export interface KGEdge {
  id: string;
  source: string;
  target: string;
  label: string;
  state?: NodeVisualState;
}

interface KGViewerProps {
  nodes: KGNode[];
  edges: KGEdge[];
  highlightedNodeIds?: string[];
  maxNodes?: number;
  onNodeHover?: (id: string | null) => void;
  onNodeClick?: (id: string) => void;
  onNodeDoubleClick?: (id: string) => void;
  /** Hide nodes whose className matches one of these. */
  hiddenClassNames?: ReadonlySet<string> | undefined;
}

const DEFAULT_MAX_NODES = 150;

// `cose-bilkent` adds animation / node-repulsion fields that aren't declared
// on the upstream `cytoscape` types — keep the literal here and cast at the
// call site to avoid `any` leaking elsewhere.
const LAYOUT_OPTS = {
  name: 'cose-bilkent',
  animate: false,
  nodeRepulsion: 4500,
  idealEdgeLength: 80,
  randomize: true,
} as const;

/**
 * SKILL-VIZ-01 v1.11 — force-directed knowledge-graph viewer.
 *
 * Visual states (Spec S08 §"S08 Extension 2"):
 *   - committed:      full opacity, solid border (baseline graph)
 *   - staged:         60% opacity, blue tint, solid border (other staged from same batch)
 *   - staged_failing: amber tint + dashed orange border (the failing triplet under review)
 *   - inferred:       40% opacity + dashed edge (inferred triplets)
 */
export function KGViewer({
  nodes,
  edges,
  highlightedNodeIds = [],
  maxNodes = DEFAULT_MAX_NODES,
  onNodeHover,
  onNodeClick,
  onNodeDoubleClick,
  hiddenClassNames,
}: KGViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);
  // Stash callbacks in refs so listeners attached once on mount always read
  // the latest handler without us re-binding (which would clear the canvas).
  const onNodeHoverRef = useRef(onNodeHover);
  const onNodeClickRef = useRef(onNodeClick);
  const onNodeDoubleClickRef = useRef(onNodeDoubleClick);
  onNodeHoverRef.current = onNodeHover;
  onNodeClickRef.current = onNodeClick;
  onNodeDoubleClickRef.current = onNodeDoubleClick;

  const [showInferred, setShowInferred] = useState(true);
  const [showStaged, setShowStaged] = useState(true);
  const [allowAllNodes, setAllowAllNodes] = useState(false);
  const [zoomPct, setZoomPct] = useState(100);

  const { visibleNodes, visibleEdges, pruned } = useMemo(
    () =>
      filterElements({
        nodes,
        edges,
        showInferred,
        showStaged,
        maxNodes: allowAllNodes ? Infinity : maxNodes,
        hiddenClassNames,
      }),
    [nodes, edges, showInferred, showStaged, allowAllNodes, maxNodes, hiddenClassNames],
  );

  // ── Mount Cytoscape once ─────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const cy = cytoscape({
      container: containerRef.current,
      elements: [],
      style: STYLE,
    });
    cyRef.current = cy;

    cy.on('zoom', () => setZoomPct(Math.round(cy.zoom() * 100)));
    cy.on('mouseover', 'node', (e) => onNodeHoverRef.current?.(e.target.id()));
    cy.on('mouseout', 'node', () => onNodeHoverRef.current?.(null));
    cy.on('tap', 'node', (e) => onNodeClickRef.current?.(e.target.id()));
    cy.on('dbltap', 'node', (e) => onNodeDoubleClickRef.current?.(e.target.id()));

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, []);

  // ── Incremental element sync ─────────────────────────────────
  // Diff the desired elements against what's already on the canvas and only
  // add what's new / remove what's gone. Existing nodes keep their position.
  // When new nodes are introduced we lock the existing ones and re-run the
  // cose-bilkent layout on the union — the locked nodes don't move so the
  // user's mental map is preserved.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    const desiredNodeIds = new Set(visibleNodes.map((n) => n.id));
    const desiredEdgeIds = new Set(visibleEdges.map((e) => e.id));
    const highlightedSet = new Set(highlightedNodeIds);

    cy.startBatch();
    try {
      // Remove vanished elements.
      cy.nodes().forEach((n) => {
        if (!desiredNodeIds.has(n.id())) n.remove();
      });
      cy.edges().forEach((e) => {
        if (!desiredEdgeIds.has(e.id())) e.remove();
      });

      // Add new elements + update changed data on existing ones.
      const added: ElementDefinition[] = [];
      for (const node of visibleNodes) {
        const existing = cy.getElementById(node.id);
        const data = buildNodeData(node, highlightedSet.has(node.id));
        if (existing.length === 0) {
          added.push({ data });
        } else {
          existing.data(data);
        }
      }
      for (const edge of visibleEdges) {
        const existing = cy.getElementById(edge.id);
        const data = buildEdgeData(edge);
        if (existing.length === 0) {
          added.push({ data });
        } else {
          existing.data(data);
        }
      }
      if (added.length > 0) cy.add(added);
    } finally {
      cy.endBatch();
    }

    // Layout: if everything is new (first paint), run cose-bilkent on the
    // whole graph. Otherwise lock existing nodes so only the freshly-added
    // ones get placed by the force-directed pass.
    const added = cy.elements().filter((el) => !el.scratch('_placed'));
    if (added.length === 0) return;
    const opts = LAYOUT_OPTS as unknown as cytoscape.LayoutOptions;
    // cose-bilkent's graph manager doesn't tolerate being run on a subset of
    // the graph (it walks the whole element index). Instead we always layout
    // the WHOLE cy instance and lock the already-placed nodes so they keep
    // their positions; only the new ones move.
    const placed = cy.nodes().filter((n) => Boolean(n.scratch('_placed')));
    placed.lock();
    try {
      cy.layout(opts).run();
    } finally {
      placed.unlock();
    }
    if (placed.length === 0) {
      cy.fit(undefined, 30);
    }
    added.forEach((el) => {
      el.scratch('_placed', true);
    });
  }, [visibleNodes, visibleEdges, highlightedNodeIds.join('|')]);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="h-full w-full" aria-label="Knowledge graph viewer" />

      <Toolbar
        showInferred={showInferred}
        showStaged={showStaged}
        zoomPct={zoomPct}
        onToggleInferred={() => setShowInferred((v) => !v)}
        onToggleStaged={() => setShowStaged((v) => !v)}
        onReset={() => {
          cyRef.current?.layout(LAYOUT_OPTS as unknown as cytoscape.LayoutOptions).run();
          cyRef.current?.fit(undefined, 30);
        }}
        onExport={() => {
          const png = cyRef.current?.png({ output: 'blob', bg: 'white', full: true });
          if (png instanceof Blob) downloadBlob(png, 'kg-export.png');
        }}
      />

      {pruned > 0 && (
        <div className="absolute right-3 top-3 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-small text-amber-800">
          {pruned} nodes hidden ·{' '}
          <button
            type="button"
            className="font-medium underline"
            onClick={() => setAllowAllNodes((v) => !v)}
          >
            {allowAllNodes ? 'Apply cap' : `Show all (${nodes.length})`}
          </button>
        </div>
      )}

      <Legend nodes={visibleNodes} />
    </div>
  );
}

// ─── Toolbar + legend ───────────────────────────────────────────

function Toolbar({
  showInferred,
  showStaged,
  zoomPct,
  onToggleInferred,
  onToggleStaged,
  onReset,
  onExport,
}: {
  showInferred: boolean;
  showStaged: boolean;
  zoomPct: number;
  onToggleInferred: () => void;
  onToggleStaged: () => void;
  onReset: () => void;
  onExport: () => void;
}) {
  return (
    <div className="absolute left-3 top-3 flex items-center gap-1 rounded border border-slate-200 bg-white p-1 shadow-sm">
      <Button variant="ghost" size="sm" onClick={onReset} aria-label="Reset layout">
        <RotateCcw className="h-3.5 w-3.5" aria-hidden />
      </Button>
      <Button variant="ghost" size="sm" onClick={onToggleInferred}>
        <span
          className={cn(
            'mr-1 inline-block h-2 w-2 rounded-full border',
            showInferred ? 'bg-slate-400' : 'border-slate-300 bg-transparent',
          )}
          aria-hidden
        />
        Inferred
      </Button>
      <Button variant="ghost" size="sm" onClick={onToggleStaged}>
        <span
          className={cn(
            'mr-1 inline-block h-2 w-2 rounded-full border',
            showStaged ? 'bg-blue-400' : 'border-slate-300 bg-transparent',
          )}
          aria-hidden
        />
        Staged
      </Button>
      <Button variant="ghost" size="sm" onClick={onExport} aria-label="Export PNG">
        <Download className="h-3.5 w-3.5" aria-hidden />
      </Button>
      <span className="ml-1 inline-flex items-center gap-1 text-small text-slate-500">
        <Maximize2 className="h-3 w-3" aria-hidden />
        {zoomPct}%
      </span>
    </div>
  );
}

function Legend({ nodes }: { nodes: KGNode[] }) {
  const present = new Set(nodes.map((n) => stripPrefix(n.className)));
  const shown = LEGEND_ORDER.filter((c) => present.has(c));
  if (shown.length === 0) return null;
  return (
    <div className="absolute bottom-3 right-3 rounded border border-slate-200 bg-white/95 p-2 text-small shadow">
      <p className="mb-1 font-medium text-slate-700">Classes</p>
      <ul className="space-y-0.5">
        {shown.map((c) => (
          <li key={c} className="flex items-center gap-2">
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: colorForClass(c) }}
            />
            <span className="text-slate-600">{c}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Element builders ──────────────────────────────────────────

function buildNodeData(node: KGNode, isHighlighted: boolean): Record<string, unknown> {
  const state = node.state ?? 'committed';
  return {
    id: node.id,
    label: node.label,
    className: stripPrefix(node.className),
    color: colorForClass(node.className),
    state,
    highlighted: isHighlighted,
  };
}

function buildEdgeData(edge: KGEdge): Record<string, unknown> {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    label: edge.label,
    state: edge.state ?? 'committed',
  };
}

function filterElements({
  nodes,
  edges,
  showInferred,
  showStaged,
  maxNodes,
  hiddenClassNames,
}: {
  nodes: KGNode[];
  edges: KGEdge[];
  showInferred: boolean;
  showStaged: boolean;
  maxNodes: number;
  hiddenClassNames?: ReadonlySet<string> | undefined;
}): { visibleNodes: KGNode[]; visibleEdges: KGEdge[]; pruned: number } {
  const filtered = nodes.filter((n) => {
    if (hiddenClassNames && hiddenClassNames.has(stripPrefix(n.className))) return false;
    if (n.state === 'inferred') return showInferred;
    if (n.state === 'staged') return showStaged;
    return true;
  });
  const pruned = Math.max(0, filtered.length - maxNodes);

  // Pruning strategy: keep failing nodes always, then committed, then staged, then inferred.
  const ordered = [...filtered].sort((a, b) => priority(b) - priority(a));
  const visibleNodes = ordered.slice(0, maxNodes);
  const visibleIds = new Set(visibleNodes.map((n) => n.id));
  const visibleEdges = edges.filter(
    (e) => visibleIds.has(e.source) && visibleIds.has(e.target),
  );
  return { visibleNodes, visibleEdges, pruned };
}

function priority(node: KGNode): number {
  switch (node.state) {
    case 'staged_failing':
      return 4;
    case 'committed':
      return 3;
    case 'staged':
      return 2;
    case 'inferred':
      return 1;
    default:
      return 0;
  }
}

function stripPrefix(name: string): string {
  return name.includes(':') ? (name.split(':').pop() ?? name) : name;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Cytoscape stylesheet ─────────────────────────────────────

const STYLE: cytoscape.Stylesheet[] = [
  {
    selector: 'node',
    style: {
      'background-color': 'data(color)',
      'border-color': '#0f172a',
      'border-width': 1,
      label: 'data(label)',
      color: '#0f172a',
      'font-size': 10,
      'text-valign': 'bottom',
      'text-margin-y': 4,
      'text-wrap': 'wrap',
      'text-max-width': '80px',
      width: 18,
      height: 18,
    },
  },
  {
    selector: 'node[state = "staged"]',
    style: { opacity: 0.6, 'border-color': '#3b82f6' },
  },
  {
    selector: 'node[state = "staged_failing"]',
    style: {
      'border-style': 'dashed',
      'border-color': '#f97316',
      'border-width': 2.5,
      'overlay-color': '#fef3c7',
      'overlay-opacity': 0.3,
    },
  },
  {
    selector: 'node[state = "inferred"]',
    style: { opacity: 0.4, 'border-style': 'dashed', 'border-color': '#94a3b8' },
  },
  {
    selector: 'node[?highlighted]',
    style: { 'border-color': '#f97316', 'border-width': 2.5 },
  },
  {
    selector: 'edge',
    style: {
      'line-color': '#cbd5e1',
      'target-arrow-color': '#cbd5e1',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      label: 'data(label)',
      'font-size': 8,
      color: '#64748b',
      'text-rotation': 'autorotate',
      'text-background-color': '#ffffff',
      'text-background-opacity': 0.7,
      width: 1.2,
    },
  },
  {
    selector: 'edge[state = "inferred"]',
    style: { 'line-style': 'dashed', opacity: 0.5 },
  },
];

const _OTHER = OTHER_CLASS_COLOR; // re-export anchor for tree-shaking
export { _OTHER as _otherClassColor };
